import { useEffect, useState } from "react";
import { getBookingManual, IBookingManualState } from "../../../../share/store/slices/booking-manual";
import { useDispatch, useSelector } from "react-redux";
import ManualBookingForm from "./manual-booking-form/component";
import { axiosInstance, FunctionalRootState, Toaster } from "@share/utils";
import ManualBookingTable from "./manual-booking-table/component";
import { Button, TablePaginationConfig } from "antd";
import { ColumnsType } from "antd/lib/table";
import ManualBookingFilters from "./manual-booking-filters/component";
import { IMenuState } from "@share/store/slices";
import { useIntl } from "react-intl";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import { FilterDropdownProps } from "antd/lib/table/interface";

export interface ManualBookingData {
  manualBookingId: number;
  agentName: string;
  agentID: number;
  contactInformation: string;
  bookingId: number;
  bookingDate: string;
  travelDateStart: string;
  travelDateEnd: string;
  refundAdjustment: string;
  bookingType: string;
  clientName: string;
  serviceProviderName: string;
  serviceConfirmationNumber: string;
  numberOfTravelers: number;
  detailsOfServices: string;
  totalBookingAmount: string;
  totalCommission: string;
  refundsOrAdjustments: string;
  dateOfSubmission: string;
  created: string;
  lastUpdated: string;
  pathFile: string;
  userId: string;
  accountId: string;
}

export interface IFiltersManualBooking {
  agentName: string;
  bookingId: string;
  manualBookingId?: number;
  paymentStatus?: string;
  isPaid?: boolean;
}

export interface IBookingManualSort {
  sortOrder: string | null, 
  orderBy: string | null
}

export function DashboardManualBooking() {

  const dispatch = useDispatch();
  const bookingManualStore: IBookingManualState = useSelector(
    (state: FunctionalRootState) => state.bookingManualStore,
  );

  const { bookingManual } = bookingManualStore;

  const { items }: IMenuState = useSelector(
    (state: FunctionalRootState) => state.navigationMenuStore,
  );

  const intl = useIntl();

  const agentID = items?.rsiId ?? 0;

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: bookingManual?.totalResults,
  });

  const [sort, setSort] = useState<IBookingManualSort>({sortOrder: 'agentName', orderBy: 'desc'})

  const [filters, setFilters] = useState<IFiltersManualBooking>({
    agentName: '',
    bookingId: '',
  });

  useEffect(() => {
    dispatch(getBookingManual(pagination, agentID, sort, filters));
  }, [dispatch, pagination.current, pagination.pageSize, sort])

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      total: bookingManual?.totalResults,
    }));
  }, [bookingManual?.totalResults]);

  const columns: ColumnsType<ManualBookingData> = [
    {
      title: intl.formatMessage({ id: 'bookingmanual.manualBookingId' }),
      dataIndex: 'manualBookingId',
      key: 'manualBookingId',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: intl.formatMessage({ id: "bookingmanual.agentName" }),
      dataIndex: "agentName",
      key: "agentName",
      sorter: true,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: intl.formatMessage({ id: "bookingmanual.bookingId" }),
      dataIndex: "bookingId",
      key: "bookingId",
      sorter: true,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: intl.formatMessage({ id: "bookingmanual.numberOfTravelersRooms" }),
      dataIndex: "numberOfTravelers",
      key: "numberOfTravelers",
      sorter: true,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: intl.formatMessage({ id: "bookingmanual.travelDateStart" }),
      dataIndex: "travelDateStart",
      key: "travelDateStart",
      render: (text) => {
        return moment(text, 'yyyy-MM-DD').format('MM-DD-yyyy');
      },
      sorter: true,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: intl.formatMessage({ id: "bookingmanual.travelDateEnd" }),
      dataIndex: "travelDateEnd",
      key: "travelDateEnd",
      render: (text) => {
        return moment(text, 'yyyy-MM-DD').format('MM-DD-yyyy');
      },
      sorter: true,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: 'Paid Status',
      dataIndex: 'isPaid',
      key: 'isPaid',
      sorter: true,
      sortDirections: ["descend", "ascend"],
      render: (text) => {
        return text ? 
          <div style={{ color: 'green', fontWeight: 'bold' }}>Paid</div> : 
          <div style={{ color: 'red', fontWeight: 'bold' }}>Unpaid</div>;
      },
    },
  ];

  const handleDownloadFile = async (manualBooking: any) => {
    try {
      const response = await axiosInstance.get(`/manualbooking/${manualBooking?.manualBookingId}/file`, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', manualBooking?.fileName);
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      console.error(error);
      Toaster.error(intl.formatMessage({ id: 'bookingmanual.pathFile.download.error' }));
    }
  };

  const allColumns: ColumnsType<ManualBookingData> = [
    ...columns,
    {
      title: intl.formatMessage({ id: "bookingmanual.bookingStatus" }),
      dataIndex: "refundAdjustment",
      key: "refundAdjustment",
      sorter: true,
      sortDirections: ["descend", "ascend"],
      render: (text) => {
        return text === 'NewBooking' ? 'New' : text === 'ChangeBooking' ? 'Updated' : text === 'CancellationBooking' ? 'Cancellation' : '';
      },
    },
    {
      title: intl.formatMessage({ id: 'bookingmanual.contactInformation' }),
      dataIndex: 'contactInformation',
      key: 'contactInformation',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: intl.formatMessage({ id: 'bookingmanual.bookingDate' }),
      dataIndex: 'bookingDate',
      key: 'bookingDate',
      render: (text) => {
        return moment(text, 'yyyy-MM-DD').format('MM-DD-yyyy');
      },
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: intl.formatMessage({ id: 'bookingmanual.bookingType' }),
      dataIndex: 'bookingType',
      key: 'bookingType',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: intl.formatMessage({ id: 'bookingmanual.clientName' }),
      dataIndex: 'clientName',
      key: 'clientName',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: intl.formatMessage({ id: 'bookingmanual.serviceProviderName' }),
      dataIndex: 'serviceProviderName',
      key: 'serviceProviderName',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: intl.formatMessage({ id: 'bookingmanual.serviceConfirmationNumber' }),
      dataIndex: 'serviceConfirmationNumber',
      key: 'serviceConfirmationNumber',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: intl.formatMessage({ id: 'bookingmanual.detailsOfServices' }),
      dataIndex: 'detailsOfServices',
      key: 'detailsOfServices',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: intl.formatMessage({ id: 'bookingmanual.totalBookingAmount' }),
      dataIndex: 'totalBookingAmount',
      key: 'totalBookingAmount',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: intl.formatMessage({ id: 'bookingmanual.totalCommission' }),
      dataIndex: 'totalCommission',
      key: 'totalCommission',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: intl.formatMessage({ id: 'bookingmanual.refundsOrAdjustments.new' }),
      dataIndex: 'refundsOrAdjustments',
      key: 'refundsOrAdjustments',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: intl.formatMessage({ id: 'bookingmanual.dateOfSubmission' }),
      dataIndex: 'dateOfSubmission',
      key: 'dateOfSubmission',
      render: (text) => {
        return moment(text, 'yyyy-MM-DD').format('MM-DD-yyyy');
      },
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: intl.formatMessage({ id: 'bookingmanual.created' }),
      dataIndex: 'created',
      key: 'created',
      render: (text) => {
        return moment(text, 'yyyy-MM-DD').format('MM-DD-yyyy');
      },
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: intl.formatMessage({ id: 'bookingmanual.lastUpdated' }),
      dataIndex: 'lastUpdated',
      key: 'lastUpdated',
      render: (text) => {
        return moment(text, 'yyyy-MM-DD').format('MM-DD-yyyy');
      },
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: intl.formatMessage({ id: 'bookingmanual.pathFile' }),
      dataIndex: 'pathFile',
      key: 'pathFile',
      render: (_text, record) => {
        return <label style={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline' }} onClick={() => handleDownloadFile(record)}>{intl.formatMessage({ id: 'bookingmanual.pathFile.download' })}</label>;
      },
    },
  ];

  const [columnsCheck, setColumnsCheck] = useState<ColumnsType<ManualBookingData>>(columns);

  const onSubmitSearch = () => dispatch(getBookingManual(pagination, agentID, sort, filters));
  const onSubmitReset = () => dispatch(getBookingManual(pagination, agentID, sort));

  const handleSetFilters = (f: any) => {
    const isPaid = f.isPaid?.[0];
    setFilters({ ...filters, isPaid });
  };

  return (
    <>
      <ManualBookingForm bookingManualStore={bookingManualStore} pagination={pagination} agentID={agentID} filters={filters} sort={sort}/>

      <ManualBookingFilters
        filters={filters}
        setFilters={setFilters}
        onSubmitSearch={onSubmitSearch}
        onSubmitReset={onSubmitReset}
        columns={columns}
        allColumns={allColumns}
        setColumnsCheck={setColumnsCheck}
      />
      
      <ManualBookingTable
        bookingManualStore={bookingManualStore}
        pagination={pagination}
        setPagination={setPagination}
        setSort={setSort}
        setFilters={handleSetFilters}
        columns={columnsCheck}
      />
    </>
  );
}

